import { ColumnConfig, RouteEnum } from "layout/TableConstructor/types";
import { PlatformVacancyCompanyCandidateListOrderFields } from "@generated/graphql";
import dayjs from "dayjs";
import {
  VacancyCompanyCandidateItemWithAssessmentCV,
  VacancyCompanyCandidateFilter,
  VacancyCompanyCandidatesRefetch,
} from "pages/Vacancies/view/types";
import { Checkbox, Stack } from "@mui/material";
import { toggleItemInArray } from "helpers/index";
import DownloadFileButton from "components/buttons/DownloadFileButton";
import DeleteCandidateButton from "../../DeleteCandidateButton";

type GetColumnConfigParams = {
  setVacancyCompanyCandidatesRefetch: VacancyCompanyCandidatesRefetch;
  selectedCandidates: string[];
  setSelectedCandidates: React.Dispatch<React.SetStateAction<string[]>>;
};

const getColumnConfig = ({
  setVacancyCompanyCandidatesRefetch,
  selectedCandidates,
  setSelectedCandidates,
}: GetColumnConfigParams): ColumnConfig<
  VacancyCompanyCandidateItemWithAssessmentCV,
  VacancyCompanyCandidateFilter,
  PlatformVacancyCompanyCandidateListOrderFields,
  undefined,
  RouteEnum.VacancyCandidate
>[] => [
  {
    name: "checkbox",
    title: "Выбор",
    renderTitle: (data) => {
      return (
        <Checkbox
          color='primary'
          size='small'
          checked={selectedCandidates.length !== 0}
          sx={{ display: "block", width: "fit-content", marginLeft: "auto", marginRight: "auto" }}
          onClick={(e) => {
            e.stopPropagation();

            if (selectedCandidates.length === 0) {
              setSelectedCandidates(data.map((item) => item.companyCandidate.id));
            } else {
              setSelectedCandidates([]);
            }
          }}
        />
      );
    },
    render: (data) => {
      if (!data.currentCvCheck?.score) return "";

      return (
        <Checkbox
          color='primary'
          size='small'
          checked={selectedCandidates.includes(data.companyCandidate.id)}
          sx={{ display: "block", width: "fit-content", marginLeft: "auto", marginRight: "auto" }}
          onClick={(e) => {
            e.stopPropagation();

            const newSelected = toggleItemInArray(selectedCandidates, data.companyCandidate.id);
            setSelectedCandidates(newSelected);
          }}
        />
      );
    },
  },
  {
    name: "numericId",
    title: "ID кандидата",
    filterName: "numericId",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateNumericId,
    render: (data) => data.companyCandidate.numericId,
  },
  {
    name: "createdAt",
    title: "Дата создания",
    filterName: "createdAt",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCreatedAt,
    render: (data) => (data.createdAt ? dayjs(data.createdAt).format("DD.MM.YYYY") : "-"),
  },
  {
    name: "telegramUsername",
    title: "Ник Telegram",
    filterName: "telegramUsername",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCandidateUsernameTelegramUsername,
    render: (data) => data.companyCandidate.candidateUsername?.telegramUsername,
  },
  {
    name: "login",
    title: "Логин",
    filterName: "login",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCandidateUsernameLogin,
    render: (data) => data.companyCandidate.candidateUsername?.login || "-",
  },
  {
    name: "fullName",
    title: "ФИО кандидата",
    filterName: "fullName",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateFullName,
    render: (data) => data.companyCandidate.fullName || "-",
  },
  {
    name: "isCVFile",
    title: "Файл резюме",
    filterName: "isCVFile",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCvFileId,
    render: (data) => (data.companyCandidate.cvFileId ? "Да" : "Нет"),
  },
  {
    name: "cvCheckScore",
    title: "Соответствие вакансии",
    filterName: "cvCheckScore",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CvChecksScore,
    render: (data) => data.currentCvCheck?.score ?? "-",
  },
  {
    name: "action",
    title: "Действие",
    render: (data) => {
      const cvReportUrl = data.currentCvCheck?.reportFile?.url;

      return (
        <Stack
          direction='row'
          sx={{ justifyContent: "space-between", alignItems: "center" }}>
          {cvReportUrl && (
            <DownloadFileButton
              href={cvReportUrl!}
              tooltipText='Отчет по CV'
            />
          )}
          <DeleteCandidateButton
            candidateId={data.id}
            setVacancyCompanyCandidatesRefetch={setVacancyCompanyCandidatesRefetch}
          />
        </Stack>
      );
    },
  },
  {
    name: "totalExperienceYears",
    title: "Стаж",
    filterName: "totalExperienceYears",
    render: (data) => {
      return data.currentCvCheck?.assessmentCV?.totalExperienceYears ?? "-";
    },
  },
  {
    name: "criterionMatch_experience",
    title: 'Критерий "Опыт"',
    filterName: "criterionMatch_experience",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[0].score ?? "-",
  },
  {
    name: "criteria_experience_text",
    title: 'Критерий "Опыт" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[0].explanation ?? "-",
  },
  {
    name: "criterionMatch_relevance",
    title: 'Критерий "Релевантность"',
    filterName: "criterionMatch_relevance",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[1].score ?? "-",
  },
  {
    name: "criteria_relevance_text",
    title: 'Критерий "Релевантность" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[1].explanation ?? "-",
  },
  {
    name: "criterionMatch_skills",
    title: 'Критерий "Навыки и сертификаты"',
    filterName: "criterionMatch_skills",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[2].score ?? "-",
  },
  {
    name: "criteria_skills_text",
    title: 'Критерий "Навыки и сертификаты" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[2].explanation ?? "-",
  },
  {
    name: "criterionMatch_resume",
    title: 'Критерий "Оформление резюме"',
    filterName: "criterionMatch_resume",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[3].score ?? "-",
  },
  {
    name: "criteria_resume_text",
    title: 'Критерий "Оформление резюме" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[3].explanation ?? "-",
  },
  {
    name: "criterionMatch_projects",
    title: 'Критерий "Проекты и достижения"',
    filterName: "criterionMatch_projects",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[4].score ?? "-",
  },
  {
    name: "criteria_projects_text",
    title: 'Критерий "Проекты и достижения" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[4].explanation ?? "-",
  },
  {
    name: "criterionMatch_soft_skills",
    title: 'Критерий "Софт-скиллы"',
    filterName: "criterionMatch_soft_skills",
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[5].score ?? "-",
  },
  {
    name: "criteria_soft_skills_text",
    title: 'Критерий "Софт-скиллы" (комментарий)',
    render: (data) => data.currentCvCheck?.assessmentCV?.criterionMatch[5].explanation ?? "-",
  },
  {
    name: "advantages",
    title: "Ключевые плюсы кандидата",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.advantages.join(".\n\n") || "-",
  },
  {
    name: "disadvantages",
    title: "Ключевые минусы кандидата",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.disadvantages.join(".\n\n") || "-",
  },
  {
    name: "recommendations",
    title: "Рекомендации",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.recommendations.join(".\n\n") || "-",
  },
];

export default getColumnConfig;
